<script lang="ts">
  import { onMount } from "svelte"
  import Autocomplete from "$app/components/Autocomplete.svelte"
  import { xhr, toast } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import Modal from "$app/components/Modal.svelte"
  import SymbolPicker from "$app/Pages/Plans/OnePageView/SymbolPicker.svelte"
  import { Pen, Check } from "lucide-svelte"
  import { onboardingStore } from "../onboarding.svelte"
  import { appStore } from "$app/store.svelte"
  import Tiptap from "$app/components/Tiptap.svelte"
  import { onlyTextButtons } from "$app/components/Tiptap/buttons"

  let props = $props()

  let {
    onactive,
    onprocess
  }: {
    onactive?: (string) => void
    onprocess?: (valid: boolean) => void
  } = props

  let statement: StatementType = $state(props.statement)
  let index: number = $state(props.index)
  let active: boolean = $state(props.active || false)

  let openModal = $state(false)

  let form = $state({})
  let errors: any = $state({})

  let saving = false

  async function createStatement(): Promise<void> {
    try {
      onprocess(validValue())
      saving = true
      const resp = await xhr.post<{ statement: StatementType }>(
        `/plans/${appStore.plan.id}/statements.json`,
        {
          statement: {
            name: form.name,
            description: form.description,
            section_type: statement.section_type
          }
        }
      )

      statement = resp.data.statement
    } catch (e: any) {
      toast(`There was an error saving your statement ${form.name}.`, "error")
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    } finally {
      saving = false
      onprocess(validValue())
    }
  }

  async function updateStatement(): Promise<void> {
    try {
      onprocess(validValue())
      const resp = await xhr.put<StatementType>(
        `/plans/${appStore.plan.id}/statements/${statement.id}.json`,
        {
          statement: {
            name: form.name,
            description: form.description
          }
        }
      )

      statement = resp.data
    } catch (e) {
      toast(`There was an error saving your statement ${form.name}.`, "error")
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    } finally {
      saving = false
      onprocess(validValue())
    }
  }

  function createOrUpdateStatement(field: string): void {
    if (statement.id && form[field] !== statement[field]) {
      updateStatement()
    } else if (!statement.id) {
      createStatement()
    }
  }

  function updateSymbol(img: any): void {
    statement.symbol_url = img.symbol_url
    statement.symbol_id = img.id
    openModal = false
    onprocess(validValue())
  }

  let editorNode: HTMLElement
  let editDescription = $state(false)

  function validValue(): boolean {
    return (
      Object.keys(errors).length === 0 &&
      !saving &&
      !!statement.id &&
      !!statement.symbol_id
    )
  }

  function setDescription(): void {
    if (!statement.id) return
    createOrUpdateStatement("description")
    editDescription = false
  }

  if (!active) {
    editDescription = false
  }

  let desc = $state("")

  onMount(() => {
    form = { ...statement }
    desc = JSON.parse(JSON.stringify(statement.description || ""))

    onprocess(validValue())
  })
</script>

<div
  class="mb-4 w-full"
  onclick={() => onactive("active")}
  onkeydown={(e) => {
    if (e.key === "Enter" || e.key === " ") {
      onactive("active")
    }
  }}
  role="button"
  tabindex="0"
>
  <div class="grid grid-cols-6 items-center gap-4">
    <div class="col-span-5 flex justify-start items-center py-3">
      <div
        class="p-2 mr-3 rounded-lg h-10 w-10 flex items-center justify-center bg-blue-600"
      >
        <span class="text-3xl text-white font-semibold">
          {index}
        </span>
      </div>
      <Autocomplete
        items={onboardingStore.valuesOptions}
        inputValue={form.name}
        minChars={0}
        inputClass="w-full input"
        placeholder="Type your own or select a value"
        onchange={(data) => {
          form.name = data
          createOrUpdateStatement("name")
        }}
        onselected={(data) => {
          form.name = data.name
          form.description = data.description
          createOrUpdateStatement("name")
        }}
      >
        {#snippet itemsnippet(item)}
          <div class="px-4 py-2">
            {item?.name}
          </div>
        {/snippet}
      </Autocomplete>
      {#if errors?.name}
        <div class="flex gap-2">
          <div class="text-red-500 text-sm">
            {errors.name || ""}
          </div>
          <button
            hidden={!statement.id}
            class="flex gap-1 text-blue-300 text-sm"
            onclick={() => {
              form.name = statement.name
              errors = {}
              onprocess(validValue())
            }}
            type="button"
          >
            Restore previous
          </button>
        </div>
      {/if}
    </div>
    <div class="relative" class:hidden={!statement.id}>
      {#if !statement.symbol_id}
        <div id="select-symbol-msg">
          <div
            class="whitespace-nowrap relative bg-gray-200 text-sm text-gray-700 px-2 rounded-sm border border-gray-700"
          >
            Select a symbol
          </div>
        </div>
      {/if}
      <button
        onclick={() => (openModal = true)}
        data-name="symbol-picker"
        class="border-4 rounded-full h-16 w-16 p-2"
        class:border-red-500={!statement.symbol_id}
        aria-label="Select symbol"
      >
        <img src={statement.symbol_url} title="Symbol" alt="Selected symbol" />
      </button>
    </div>
  </div>

  <div class="mt-1 pl-6" class:hidden={!statement.id}>
    <button
      type="button"
      class="relative cursor-pointer w-full text-left"
      class:hidden={editDescription}
      aria-label="Edit description"
      onclick={() => {
        console.log("edit description")
        editDescription = true
      }}
      onkeydown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          editDescription = true
        }
      }}
    >
      <span class="absolute top-4 left-1 text-blue-600 hover:text-blue-400">
        <Pen class="size-5" />
      </span>
      <div
        class="overflow-y-scroll bg-gray-100 rounded-md p-2 pl-8 text-sm h-24 mt-2"
      >
        {@html form.description || ""}
      </div>
    </button>

    <div
      id="editor-cont"
      class="bg-white relative"
      class:hidden={!editDescription}
    >
      <Tiptap
        content={statement.description}
        onupdate={({ html }) => (form.description = html)}
        placeholder="Enter a description"
        class="h-20"
        id="statement_description"
        buttons={onlyTextButtons}
        allowEmojis={true}
        onblur={(e) => setDescription()}
      />
      <button
        class="btn success text-white absolute bottom-0 right-0"
        onclick={(e) => setDescription()}
      >
        <Check />
      </button>
    </div>
  </div>
</div>

<Modal
  bind:open={openModal}
  closeButton={true}
  closeOnEsc={true}
  closeOnOuterClick={true}
>
  <SymbolPicker
    {statement}
    oncancel={() => (openModal = false)}
    onupdate={(detail) => updateSymbol(detail)}
  />
</Modal>

<style lang="postcss">
  #select-symbol-msg {
    position: absolute;
    top: -32px;
    right: -2px;
  }
  #select-symbol-msg div:after {
    border-right: 5px solid transparent;
    border-top: 8px solid #cbcbcb;
    border-left: 5px solid transparent;
    content: "";
    left: 60%;
    position: absolute;
    top: 100%;
  }
  #editor-cont #editor {
    height: 100px;
  }
</style>
